import {
  OPERATOR_TYPES_1,
  OPERATOR_TYPES_3,
  OPERATOR_TYPES_4,
  OPERATOR_TYPES_7,
} from 'dashboard/routes/dashboard/settings/automation/operators';
import filterQueryGenerator from './filterQueryGenerator';
import actionQueryGenerator from './actionQueryGenerator';

const MESSAGE_CONDITION_VALUES = [
  {
    id: 'incoming',
    name: 'Incoming Message',
  },
  {
    id: 'outgoing',
    name: 'Outgoing Message',
  },
];

const INTERNAL_NOTE_CONDITION_VALUES = [
  {
    id: 'true',
    name: 'Verdadeiro',
  },
  {
    id: 'false',
    name: 'Falso',
  },
];

const DAY_OF_WEEK_OPTIONS = [
  { id: 'monday', name: 'Segunda' },
  { id: 'tuesday', name: 'Terça' },
  { id: 'wednesday', name: 'Quarta' },
  { id: 'thursday', name: 'Quinta' },
  { id: 'friday', name: 'Sexta' },
  { id: 'saturday', name: 'Sábado' },
  { id: 'sunday', name: 'Domingo' },
];

export const PRIORITY_CONDITION_VALUES = [
  { id: 'nil', name: 'Nenhum' },
  { id: '0', name: 'Baixo' },
  { id: '1', name: 'Médio' },
  { id: '2', name: 'Alto' },
  { id: '3', name: 'Urgente' },
];

export const PREVENT_CAMPAIGN_DELIVERY_VALUES = [
  {
    id: true,
    name: 'Sim',
  },
  {
    id: false,
    name: 'Não',
  },
];

export const PREVENT_CONTACT_DELIVERY_VALUES = [
  {
    id: true,
    name: 'Sim',
  },
  {
    id: false,
    name: 'Não',
  },
];

export const PREVENT_TEMPLATE_DELIVERY_VALUES = [
  {
    id: true,
    name: 'Sim',
  },
  {
    id: false,
    name: 'Não',
  },
];

export const OPPORTUNITY_STATUS_VALUES = [
  { id: 'open', name: 'Aberto' },
  { id: 'won', name: 'Ganho' },
  { id: 'lost', name: 'Perdido' },
  { id: 'canceled', name: 'Cancelado' },
];

export const OPPORTUNITY_CURRENCY_VALUES = [
  { id: 'BRL', name: 'Real (BRL)' },
  { id: 'USD', name: 'Dólar (USD)' },
  { id: 'EUR', name: 'Euro (EUR)' },
];

export const OPPORTUNITY_SLA_STATUS_VALUES = [
  { id: 'on_time', name: 'No prazo' },
  { id: 'warning', name: 'Em alerta' },
  { id: 'overdue', name: 'Atrasado' },
];

// Every pipeline stage across the account, flattened out of the pipelines
// list (there is no dedicated "all stages" endpoint) - used by both the
// "Etapa da oportunidade" condition and the "Alterar etapa" action.
export const generateOpportunityStageOptions = (pipelines = []) => {
  return pipelines.flatMap(pipeline =>
    (pipeline.stages || []).map(stage => ({
      id: stage.id,
      name: `${stage.name} (${pipeline.name})`,
    }))
  );
};

// Loss reasons also belong to a single pipeline, same as stages above -
// used to fill in the "Motivo de perda" field the "Alterar status" action
// shows once "Perdido" is selected as the target status.
export const generateOpportunityLossReasonOptions = (pipelines = []) => {
  return pipelines.flatMap(pipeline =>
    (pipeline.loss_reasons || []).map(reason => ({
      id: reason.id,
      name: `${reason.name} (${pipeline.name})`,
      __type: 'loss_reason',
    }))
  );
};

export const MESSAGE_ACTION_VALUES = (inboxes = [], templates = []) => {
  const inboxOptions = inboxes.map(inbox => ({
    ...inbox,
    __type: 'inbox',
  }));

  const templateOptions = templates.map(template => ({
    ...template,
    __type: 'template',
  }));

  return [...inboxOptions, ...templateOptions];
};

export const getCustomAttributeInputType = key => {
  const customAttributeMap = {
    date: 'date',
    text: 'plain_text',
    list: 'search_select',
    checkbox: 'search_select',
  };

  return customAttributeMap[key] || 'plain_text';
};

export const isACustomAttribute = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return attr.attribute_key === key;
  });
};

export const getCustomAttributeListDropdownValues = (
  customAttributes,
  type
) => {
  return customAttributes
    .find(attr => attr.attribute_key === type)
    .attribute_values.map(item => {
      return {
        id: item,
        name: item,
      };
    });
};

export const isCustomAttributeCheckbox = (customAttributes, key) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === key && attr.attribute_display_type === 'checkbox'
    );
  });
};

export const isCustomAttributeList = (customAttributes, type) => {
  return customAttributes.find(attr => {
    return (
      attr.attribute_key === type && attr.attribute_display_type === 'list'
    );
  });
};

export const getOperatorTypes = key => {
  if (key === 'is_template') {
    return [
      { value: 'equal_to', label: 'Equal to' }
    ];
  }

  const operatorMap = {
    list: OPERATOR_TYPES_1,
    text: OPERATOR_TYPES_3,
    number: OPERATOR_TYPES_1,
    link: OPERATOR_TYPES_1,
    date: OPERATOR_TYPES_4,
    checkbox: OPERATOR_TYPES_1,
  };

  return operatorMap[key] || OPERATOR_TYPES_1;
};

export const generateCustomAttributeTypes = (customAttributes, type) => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      inputType: getCustomAttributeInputType(attr.attribute_display_type),
      filterOperators: getOperatorTypes(attr.attribute_display_type),
      customAttributeType: type,
    };
  });
};

export const generateConditionOptions = (options, key = 'id') => {
  return options.map(i => {
    return {
      id: i[key],
      name: i.title,
    };
  });
};

export const getActionOptions = ({ agents, teams, labels, inboxes, templates, macros, campaigns, type, customAttributes, customContactAttributes, setCloseReason, accountEvents, pipelines, temperatureOptions, activityTypeOptions }) => {
  const actionsMap = {
    assign_agent: agents,
    assign_team: teams,
    send_email_to_team: teams,
    add_label: generateConditionOptions(labels, 'title'),
    remove_label: generateConditionOptions(labels, 'title'),
    change_priority: PRIORITY_CONDITION_VALUES,
    send_whatsapp_message: MESSAGE_ACTION_VALUES(inboxes, templates),
    send_whatsapp_message_now: MESSAGE_ACTION_VALUES(inboxes, templates),
    trigger_macro: macros,
    sent_campaign_name: campaigns,
    add_custom_attribute: customAttributes,
    add_contact_custom_attribute: customContactAttributes,
    set_close_reason: setCloseReason,
    prevent_campaign_delivery: PREVENT_CAMPAIGN_DELIVERY_VALUES,
    prevent_contact_delivery: PREVENT_CONTACT_DELIVERY_VALUES,
    prevent_template_delivery: PREVENT_TEMPLATE_DELIVERY_VALUES,
    send_event_log: (accountEvents || []).map(e => ({ id: e.id, name: e.event_name })),
    change_opportunity_stage: generateOpportunityStageOptions(pipelines),
    change_opportunity_owner: agents,
    change_opportunity_status: [
      ...OPPORTUNITY_STATUS_VALUES,
      ...generateOpportunityLossReasonOptions(pipelines),
    ],
    change_opportunity_temperature: temperatureOptions,
    create_opportunity_activity: activityTypeOptions,
    create_opportunity: pipelines,
  };
  return actionsMap[type];
};

export const getConditionOptions = ({
  agents,
  booleanFilterOptions,
  campaigns,
  contacts,
  countries,
  customAttributes,
  inboxes,
  languages,
  statusFilterOptions,
  teams,
  labels,
  type,
  closingReasons,
  accountEvents,
  pipelines,
  temperatureOptions,
  activityTypeOptions,
}) => {
  if (isCustomAttributeCheckbox(customAttributes, type)) {
    return booleanFilterOptions;
  }

  if (isCustomAttributeList(customAttributes, type)) {
    return getCustomAttributeListDropdownValues(customAttributes, type);
  }

  const conditionFilterMaps = {
    status: statusFilterOptions,
    assignee_id: agents,
    contact: contacts,
    inbox_id: inboxes,
    team_id: teams,
    campaigns: generateConditionOptions(campaigns),
    browser_language: languages,
    conversation_language: languages,
    country_code: countries,
    message_type: MESSAGE_CONDITION_VALUES,
    internal_note: INTERNAL_NOTE_CONDITION_VALUES,
    priority: PRIORITY_CONDITION_VALUES,
    day_of_week: DAY_OF_WEEK_OPTIONS,
    sent_campaign_name: generateConditionOptions(campaigns),
    labels: generateConditionOptions(labels, 'id'),
    'labels:before': generateConditionOptions(labels, 'id'),
    'team_id:before': teams,
    'assignee_id:before': agents,
    last_closure_reason_id: closingReasons,
    event_id: accountEvents,
    opportunity_pipeline_id: (pipelines || []).map(p => ({
      id: p.id,
      name: p.name,
    })),
    opportunity_status: OPPORTUNITY_STATUS_VALUES,
    opportunity_stage_id: generateOpportunityStageOptions(pipelines),
    opportunity_currency: OPPORTUNITY_CURRENCY_VALUES,
    opportunity_owner_id: agents,
    opportunity_sla_status: OPPORTUNITY_SLA_STATUS_VALUES,
    opportunity_temperature: temperatureOptions,
    opportunity_activity_type: activityTypeOptions,
  };
  return conditionFilterMaps[type];
};

export const getFileName = (action, files = []) => {
  const blobId = action.action_params[0];
  if (!blobId) return '';

  if (
    action.action_name === 'send_attachment' ||
    action.action_name === 'update_contact_avatar'
  ) {
    const file = files.find(item => item.blob_id === blobId);
    if (file) return file.filename.toString();
  }

  return '';
};

export const getDefaultConditions = (eventName = '') => {
  if (eventName === 'message_created') {
    return [
      {
        attribute_key: 'message_type',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }
  if (eventName === 'conversation_opened') {
    return [
      {
        attribute_key: 'browser_language',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }
  if (eventName.startsWith('opportunity_')) {
    return [
      {
        attribute_key: 'opportunity_status',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        custom_attribute_type: '',
      },
    ];
  }
  return [
    {
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    },
  ];
};

export const getDefaultActions = (eventName = '') => {
  if (eventName.startsWith('opportunity_')) {
    return [
      {
        action_name: 'create_opportunity_note',
        action_params: [],
      },
    ];
  }
  return [
    {
      action_name: 'assign_agent',
      action_params: [],
    },
  ];
};

export const filterCustomAttributes = customAttributes => {
  return customAttributes.map(attr => {
    return {
      key: attr.attribute_key,
      name: attr.attribute_display_name,
      type: attr.attribute_display_type,
    };
  });
};

export const getStandardAttributeInputType = (automationTypes, event, key) => {
  return automationTypes[event].conditions.find(item => item.key === key)
    .inputType;
};

export const generateAutomationPayload = payload => {
  const automation = JSON.parse(JSON.stringify(payload));

  automation.conditions = automation.conditions.map(condition => {
    if (condition.attribute_key === 'priority' && Array.isArray(condition.values)) {
      return {
        ...condition,
        values: condition.values.map(value => {
          if (value === 'nil' || value === null || value === undefined) {
            return null;
          }
          return value;
        }),
      };
    }

    return condition;
  });

  automation.conditions[automation.conditions.length - 1].query_operator = null;
  automation.conditions = filterQueryGenerator(automation.conditions).payload;
  automation.actions = actionQueryGenerator(automation.actions);
  return automation;
};

export const isCustomAttribute = (attrs, key) => {
  return attrs.find(attr => attr.key === key);
};

export const generateCustomAttributes = (
  conversationAttributes = [],
  contactAttributes = [],
  conversationlabel,
  contactlabel
) => {
  const customAttributes = [];
  if (conversationAttributes.length) {
    customAttributes.push(
      {
        key: `conversation_custom_attribute`,
        name: conversationlabel,
        disabled: true,
      },
      ...conversationAttributes
    );
  }
  if (contactAttributes.length) {
    customAttributes.push(
      {
        key: `contact_custom_attribute`,
        name: contactlabel,
        disabled: true,
      },
      ...contactAttributes
    );
  }
  return customAttributes;
};
